import { useContext } from 'react';
import {
  ModalContext as AriaModalContext,
  OverlayTriggerStateContext,
  useSlottedContext,
} from 'react-aria-components';

import type { Prettify } from '@infinex-private/ts-utils/types';

import type { DialogContextValue } from './Dialog';
import { DialogContext } from './Dialog';

type DialogState = Prettify<
  {
    /** A callback that can be used to close the current dialog. */
    close: () => void;
    /** Whether the current dialog is open. */
    isOpen: boolean | undefined;
  } & DialogContextValue
>;

type AriaDialogState = {
  isOpen: boolean | undefined;
  close: () => void;
};

/**
 * A convenience hook to access dialog state. Useful in situations where a
 * component is shared between dialogs, or state must be composed with other
 * hooks. For most cases, it’s simpler to provide a render prop to the `Dialog`
 * component.
 */
export function useDialogState(): DialogState {
  const ariaDialogState = useAriaDialogState();
  const taurusDialogContext = useContext(DialogContext);
  const type = taurusDialogContext?.type;

  // Merge react-aria contexts with our own, creating a unified state object.
  return {
    close: ariaDialogState.close,
    isDismissable: taurusDialogContext?.isDismissable,
    isKeyboardDismissDisabled: taurusDialogContext?.isKeyboardDismissDisabled,
    isOpen: ariaDialogState.isOpen,
    type,
  };
}

/**
 * Same overlay/modal sources as {@link useAriaDialogState}, but returns `null`
 * outside a dialog controller — for shared UI that may render on a page.
 */
export function useOptionalAriaDialogState(): AriaDialogState | null {
  const ariaModalContext = useSlottedContext(AriaModalContext);
  const triggerContext = useContext(OverlayTriggerStateContext);

  if (triggerContext) {
    return {
      isOpen: triggerContext.isOpen,
      close: triggerContext.close,
    };
  }

  if (ariaModalContext) {
    return {
      isOpen: ariaModalContext.isOpen,
      close() {
        ariaModalContext.onOpenChange?.(false);
      },
    };
  }

  return null;
}

/** @private Merge react-aria's modal and overlay-trigger contexts. */
export function useAriaDialogState(): AriaDialogState {
  const state = useOptionalAriaDialogState();
  if (state) return state;

  throw new Error(
    'Invalid invocation of `useAriaDialogState` — check call site, must be within a dialog controller.'
  );
}
